const POLISH_PATHS: Record<string, string> = {
    '/': '/pl',
    '/about-me': '/o-mnie',
    '/competencies': '/kompetencje',
    '/mentoring': '/pl/mentoring',
    '/craft': '/rzemioslo',
    '/resources': '/zasoby',
    '/blog': '/pl/blog',
    '/blog/list': '/pl/blog/lista',
    '/privacy': '/prywatnosc',
    '/terms': '/regulamin',
    '/newsletter/confirm': '/pl/newsletter/potwierdzenie'
};

const ENGLISH_PATHS = Object.fromEntries(
    Object.entries(POLISH_PATHS).map(([englishPath, polishPath]) => [polishPath, englishPath])
) as Record<string, string>;

const normalizeLanguage = (value: string): string => {
    const normalized = value.trim().toLowerCase();
    return normalized.startsWith('pl') ? 'pl' : 'en';
};

const splitPath = (value: string): [string, string] => {
    const queryIndex = value.indexOf('?');
    const hashIndex = value.indexOf('#');
    const suffixIndex = queryIndex >= 0 && hashIndex >= 0
        ? Math.min(queryIndex, hashIndex)
        : Math.max(queryIndex, hashIndex);

    if (suffixIndex < 0) {
        return [value, ''];
    }

    return [value.slice(0, suffixIndex), value.slice(suffixIndex)];
};

const normalizePath = (path: string): string => {
    if (!path || path === '/') {
        return '/';
    }

    const withSlash = path.startsWith('/') ? path : `/${path}`;
    const trimmed = withSlash.replace(/\/+$/, '');
    return trimmed || '/';
};

export const getCurrentCulture = (): string =>
    normalizeLanguage(document.body.dataset.culture || document.documentElement.lang || 'en');

export const localizedPath = (path: string, culture = getCurrentCulture()): string => {
    const targetIsPolish = normalizeLanguage(culture) === 'pl';
    const [rawPath, suffix] = splitPath(path);
    const normalizedPath = normalizePath(rawPath);

    if (targetIsPolish) {
        if (POLISH_PATHS[normalizedPath]) {
            return `${POLISH_PATHS[normalizedPath]}${suffix}`;
        }

        if (normalizedPath.startsWith('/craft/projects/')) {
            return `/rzemioslo/projekty/${normalizedPath.slice('/craft/projects/'.length)}${suffix}`;
        }

        return `${normalizedPath}${suffix}`;
    }

    if (ENGLISH_PATHS[normalizedPath]) {
        return `${ENGLISH_PATHS[normalizedPath]}${suffix}`;
    }

    if (normalizedPath.startsWith('/rzemioslo/projekty/')) {
        return `/craft/projects/${normalizedPath.slice('/rzemioslo/projekty/'.length)}${suffix}`;
    }

    return `${normalizedPath}${suffix}`;
};

export const projectPath = (slug: string, culture = getCurrentCulture()): string => {
    const escapedSlug = encodeURIComponent(slug);
    return normalizeLanguage(culture) === 'pl'
        ? `/rzemioslo/projekty/${escapedSlug}`
        : `/craft/projects/${escapedSlug}`;
};

export const newsletterConfirmPath = (): string =>
    localizedPath('/newsletter/confirm');

export const cultureHeaders = (culture = getCurrentCulture()): HeadersInit => ({
    Accept: 'application/json',
    'X-Rinkachi-Culture': culture
});
